// ConfigBundle: one version of every behavior kind, pinned. A consult
// snapshots its bundle at check-in and never changes it mid-call (design
// 9.11); the package records the pins so any report traces to the exact
// words Otto ran with.
import { z } from "zod";
import { Agenda, agendaStageIds, agendaTotalMinutes } from "./agenda";
import { OttoBrief } from "./brief";
import { CoachConfig } from "./coach";
import { type ConfigKind, CONFIG_KINDS, VersionNumber } from "./common";
import { ConductRules } from "./conduct";
import { FactsBlock } from "./facts";
import { InfoMap } from "./info-map";
import { Timing } from "./timing";
import { VoiceSettings } from "./voice";

/** The body schema for each versioned kind. */
export const ConfigBodySchemas = {
  brief: OttoBrief,
  conduct: ConductRules,
  info_map: InfoMap,
  agenda: Agenda,
  facts: FactsBlock,
  timing: Timing,
  voice: VoiceSettings,
  coach: CoachConfig,
} as const satisfies Record<ConfigKind, z.ZodType>;

export type ConfigBody<K extends ConfigKind> = z.infer<(typeof ConfigBodySchemas)[K]>;

export const ConfigPins = z.object({
  brief: VersionNumber,
  conduct: VersionNumber,
  info_map: VersionNumber,
  agenda: VersionNumber,
  facts: VersionNumber,
  timing: VersionNumber,
  voice: VersionNumber,
  coach: VersionNumber,
});
export type ConfigPins = z.infer<typeof ConfigPins>;

export const ConfigBundle = z.object({
  pins: ConfigPins,
  brief: OttoBrief,
  conduct: ConductRules,
  info_map: InfoMap,
  agenda: Agenda,
  facts: FactsBlock,
  timing: Timing,
  voice: VoiceSettings,
  coach: CoachConfig,
});
export type ConfigBundle = z.infer<typeof ConfigBundle>;

export type LoadResult<T> = { ok: true; value: T; warnings: string[] } | { ok: false; issues: string[] };

function formatIssues(error: z.ZodError, prefix = ""): string[] {
  return error.issues.map((issue) => `${prefix}${issue.path.join(".") || "(root)"}: ${issue.message}`);
}

/** Validate one version body of one kind (used on every save in Otto Studio). */
export function parseConfigBody<K extends ConfigKind>(kind: K, body: unknown): LoadResult<ConfigBody<K>> {
  const result = ConfigBodySchemas[kind].safeParse(body);
  if (!result.success) return { ok: false, issues: formatIssues(result.error, `${kind}.`) };
  return { ok: true, value: result.data as ConfigBody<K>, warnings: [] };
}

/**
 * Validate a whole bundle: every part against its schema, then the checks
 * that span kinds. Failures are returned, never thrown, so the admin can show
 * all of them at once; callers that run a consult must refuse a failed load.
 */
export function loadConfigBundle(input: unknown): LoadResult<ConfigBundle> {
  const parsed = ConfigBundle.safeParse(input);
  if (!parsed.success) return { ok: false, issues: formatIssues(parsed.error) };
  const bundle = parsed.data;
  const issues: string[] = [];
  const warnings: string[] = [];

  const stageIds = new Set(agendaStageIds(bundle.agenda));
  for (const stage of bundle.coach.silent_stage_ids) {
    if (!stageIds.has(stage)) issues.push(`coach.silent_stage_ids: ${stage} is not a stage in the agenda`);
  }

  const agendaMinutes = agendaTotalMinutes(bundle.agenda);
  if (agendaMinutes > bundle.timing.cap_minutes) {
    issues.push(`agenda: items add up to ${agendaMinutes} minutes, over the ${bundle.timing.cap_minutes} minute cap`);
  } else if (Math.abs(agendaMinutes - bundle.timing.target_minutes) > 1) {
    warnings.push(`agenda: items add up to ${agendaMinutes} minutes; the target is ${bundle.timing.target_minutes}`);
  }

  if (bundle.info_map.facts.every((f) => f.tier !== "MUST")) {
    warnings.push("info_map: no MUST facts, so the readiness rule has nothing to check");
  }

  const roles = stageRoles(bundle);
  if (roles.readback === null) warnings.push("agenda: no read-back stage (set agenda.readback_stage_id, or list it first in coach.silent_stage_ids)");
  for (const [role, id] of [["read-back", roles.readback], ["close", roles.close]] as const) {
    if (id !== null && !bundle.coach.silent_stage_ids.includes(id)) {
      warnings.push(`coach.silent_stage_ids: the ${role} stage ${id} is not silent, so coach notes may reach Otto during it`);
    }
  }
  if (!bundle.conduct.guard_lists) {
    warnings.push("conduct.guard_lists: missing, so the output guard cannot run and nothing is approved on its word");
  }
  if (!bundle.coach.writer) {
    warnings.push("coach.writer: missing, so the package writer is skipped and packages use the ledger or the transcript only");
  }

  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, value: bundle, warnings };
}

export interface StageRoles {
  /** The read-back stage id, or null when the config names none. */
  readback: string | null;
  /** The close stage id, or null. */
  close: string | null;
}

/**
 * Which stages are the read-back and the close. Explicit in the agenda when
 * set; otherwise the first and the last of coach.silent_stage_ids in agenda
 * order (the close only when it differs from the read-back).
 */
export function stageRoles(bundle: Pick<ConfigBundle, "agenda" | "coach">): StageRoles {
  const stages = agendaStageIds(bundle.agenda);
  const silent = stages.filter((id) => bundle.coach.silent_stage_ids.includes(id));
  const readback = bundle.agenda.readback_stage_id ?? silent[0] ?? null;
  const lastSilent = silent[silent.length - 1] ?? null;
  const close = bundle.agenda.close_stage_id ?? (lastSilent !== null && lastSilent !== readback ? lastSilent : null);
  return { readback, close };
}

/** One stored version of one kind, as read from config_versions. */
export interface StoredVersion {
  kind: ConfigKind;
  version: number;
  body: unknown;
}

/**
 * Assemble and validate a bundle from one stored version per kind (usually
 * the active ones). Missing or duplicate kinds are issues.
 */
export function bundleFromVersions(rows: readonly StoredVersion[]): LoadResult<ConfigBundle> {
  const issues: string[] = [];
  const byKind = new Map<ConfigKind, StoredVersion>();
  for (const row of rows) {
    if (byKind.has(row.kind)) issues.push(`${row.kind}: more than one version given`);
    byKind.set(row.kind, row);
  }
  for (const kind of CONFIG_KINDS) {
    if (!byKind.has(kind)) issues.push(`${kind}: no version given`);
  }
  if (issues.length > 0) return { ok: false, issues };

  const candidate: Record<string, unknown> = { pins: {} };
  for (const kind of CONFIG_KINDS) {
    const row = byKind.get(kind)!;
    (candidate.pins as Record<string, number>)[kind] = row.version;
    candidate[kind] = row.body;
  }
  return loadConfigBundle(candidate);
}
